<template>
  <PlainCard>
    <FormBuilder
      v-bind="formProps"
      :inputProperties="resultInputProperties"
      @submit="onSubmit"
    >
      <template #belowForm>
        <v-alert
          v-if="registeredUserInfo"
          type="success"
          :value="true"
          closable
          @update:model-value="registeredUserInfo = null"
        >
          {{ lt('account_created') }}<br>
          {{ lt('id_of_created_user') }}: {{ registeredUserInfo.accountId }}<br>
        </v-alert>
      </template>
    </FormBuilder>

    <v-dialog v-model="otpDialogOpen" persistent max-width="480">
      <v-card class="pa-3">
        <FieldConfirmation class="pa-2 border-sm rounded-lg"
          v-if="currentOtpAlias"
          :key="currentOtpAlias"
          :alias="currentOtpAlias"
          :value="currentOtpValue"
          :optionalConfirm="isCurrentOtpOptional"
          :updateProfile="false"
          @confirmed="onOtpConfirmed"
        />
        <v-card-actions>
          <v-btn
            v-if="isCurrentOtpOptional"
            variant="text"
            @click="onOtpSkip"
          >
            {{ lt('skip') }}
          </v-btn>
          <v-spacer />
          <v-btn
            variant="text"
            @click="onOtpCancel"
          >
            {{ lt('cancel') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </PlainCard>
</template>

<script setup lang="ts">
import { onMounted, ref } from 'vue';
import { RegisterMemberProps, ConfirmationMode, defaults } from './RegisterMember.ts';

import { FormResult, genFormFieldFromAlias } from '@/models/form-builder-interface';
import { CONFIRMABLE_FIELDS_ALIASES, ConfirmableFieldAlias, ConfirmationResult } from '@/models/field-confirmation-interface';

import { useAccountApi, useAccountCreationApi } from '@ui-api3-sdk/api/api3';
import { useAwaitAccountId, useEnrollSettings, useFormBuilder, useLocalization, useToast } from '@/composables';
import { usePropertiesStore } from '@/pinia/usePropertiesStore';
import { resolveListStatic } from '@/utils/properties-mapping-service';
import { generatePassword } from '@/utils/utils';

import FormBuilder from './base/FormBuilder.vue';
import FieldConfirmation from './base/FieldConfirmation.vue';
import PlainCard from './base/PlainCard.vue';

const { lt } = useLocalization('uiRegisterMember', [
  'email_not_unique',
  'phone_not_unique',
  'account_created',
  'unknown_uniqueness_error',
  'unknown_create_error',
  'id_of_created_user',
  'skip',
  'cancel',
]);

const props = withDefaults(defineProps<RegisterMemberProps>(), {
  id: undefined,
  fieldTitles: undefined,
  optionalFields: undefined,
  forceFields: undefined,
  otpConfirm: undefined,
  profileFields: () => defaults.profileFields,
  inputProperties: () => defaults.inputProperties,
});

const { inputProperties: resultInputProperties } = useEnrollSettings(props.inputProperties);
const { props: formProps, methods: form } = useFormBuilder([]);

type RegisteredUserInfo = {
  name: string,
  accountId: string;
}

const CUSTOM_FIELDS = resolveListStatic('custom').map(p => p.replace('custom.', ''));

const registeredUserInfo = ref<RegisteredUserInfo | null>(null);

// OTP state
const otpDialogOpen = ref(false);
const otpQueue = ref<ConfirmableFieldAlias[]>([]);
const currentOtpAlias = ref<ConfirmableFieldAlias | null>(null);
const otpHeaders = new Map<ConfirmableFieldAlias, ConfirmationResult>();
let pendingProfile: Record<string, any> | null = null;

const currentOtpValue = ref<string | undefined>(undefined);
const isCurrentOtpOptional = ref(false);

function isCustomProperty(alias: string): boolean {
  return CUSTOM_FIELDS.includes(alias);
}

onMounted(async () => {
  await usePropertiesStore().getPfAsync();

  const fields = props.profileFields.map(alias => {
    const field = {
      ... genFormFieldFromAlias(`${ isCustomProperty(alias) ? 'custom' : 'p' }.${alias}`),
      isMandatory: ! isFieldOptional(alias),
      alias,
    };
    if (props.fieldTitles?.[alias]) {
      field.title = props.fieldTitles[alias];
    }
    return field;
  });

  form.setFields(fields);
});

function getFormValues(res: FormResult): Record<string, any> {
  const profile = { ...res };
  Object.keys(profile).forEach(k => { if (!profile[k]) delete profile[k]; });
  CUSTOM_FIELDS.forEach(cf => delete profile[cf]);
  Object.entries(props.forceFields || {}).forEach(([k, v]) => { profile[k] = v; });
  return profile;
}

const onSubmit = async (res: FormResult) => {
  const { phone, email } = res;

  form.setLoading(true);

  const uniqueCheckResult = await useAccountApi()
    .otpUniquenessCheck({
      uniquenessCheckReq: { phone, email },
    })
    .catch(err => {
      if (form.parseApiValidation(err)) return;
      form.addError(lt('unknown_uniqueness_error'));
    })
    .finally(() => {
      form.setLoading(false);
    });

  if (!uniqueCheckResult?.data?.payload) return;
  const { emailUnique, phoneUnique } = uniqueCheckResult?.data?.payload || {};

  if ( email && emailUnique === false ) form.addFieldError('email', lt('email_not_unique'));
  if ( phone && phoneUnique === false ) form.addFieldError('phone', lt('phone_not_unique'));

  if ( form.hasErrors() ) return;

  const profile = getFormValues(res);

  const confirmConfig = props.otpConfirm;
  if (confirmConfig) {
    const queue = CONFIRMABLE_FIELDS_ALIASES.filter(alias => {
      const mode = confirmConfig[alias];
      if (!mode) return false;
      return !!res[alias];
    });

    if (queue.length) {
      otpHeaders.clear();
      pendingProfile = profile;
      otpQueue.value = queue;
      openNextOtpDialog();
      return;
    }
  }

  await createAccount(profile);
};

function openNextOtpDialog() {
  const alias = otpQueue.value[0];
  if (!alias) {
    otpDialogOpen.value = false;
    if (pendingProfile) createAccount(pendingProfile);
    return;
  }

  const mode = props.otpConfirm?.[alias] as ConfirmationMode;
  currentOtpAlias.value = alias;
  currentOtpValue.value = form.hasField(alias) ? form.getField(alias).value : undefined;
  isCurrentOtpOptional.value = mode === 'optional';
  otpDialogOpen.value = true;
}

function onOtpConfirmed(result: ConfirmationResult) {
  otpHeaders.set(result.field, result);
  otpQueue.value = otpQueue.value.slice(1);
  openNextOtpDialog();
}

function onOtpSkip() {
  otpQueue.value = otpQueue.value.slice(1);
  openNextOtpDialog();
}

function onOtpCancel() {
  otpDialogOpen.value = false;
  currentOtpAlias.value = null;
  otpQueue.value = [];
  pendingProfile = null;
  otpHeaders.clear();
  form.setLoading(false);
}

async function createAccount(profile: Record<string, any>) {
  form.setLoading(true);

  const aId = await useAwaitAccountId();

  const gluedOtpHeaders = Array.from(otpHeaders.values())
    .map(h => h.otpHeader['x-otp-check'])
    .join('|');

  const requestOptions = gluedOtpHeaders
    ? { headers: { 'x-otp-check': gluedOtpHeaders } }
    : undefined;

  useAccountCreationApi()
    .accountCreateControllerCreateAccount({
      accountCreateReq: {
        login: profile.email,
        password: getPasswordValue(),
        sponsorId: aId,
        profile,
      },
    }, requestOptions)
    .then((res) => {
      const info = res.data.payload.info as any;

      registeredUserInfo.value = {
        name:      info.title as string,
        accountId: info.account.id as string,
      };

      useToast().ok(lt('account_created'));
      form.reset();
      form.clearErrors();
      otpHeaders.clear();
      pendingProfile = null;
    })
    .catch(err => {
      if (form.parseApiValidation(err)) return;
      form.addError(lt('unknown_create_error'));
    })
    .finally(() => {
      form.setLoading(false);
    });
}

function getPasswordValue() {
  if (form.hasField('password')) return form.getField('password').value;

  return generatePassword({
    size: props.inputProperties?.passwordMinLength || 6,
    hasUpper: props.inputProperties.passwordHasCamelcase || false,
    hasSpecial: props.inputProperties.passwordHasSpecSymbols || false,
  });
}

function isFieldOptional(alias: string) {
  return props.optionalFields?.includes(alias);
}

</script>
